import { CustomField, fieldProps, lookupPipeline, renderLink } from './helpers'

const renderCandidateLink = renderLink('/cat/candidates')

const statusPipeline = lookupPipeline('candidate_statuses', 'status')
const substatusPipeline = lookupPipeline('candidate_substatuses', 'substatus')
const rankPipeline = lookupPipeline('candidate_ranks', 'rank')
const clearancePipeline = lookupPipeline('clearances', 'clearance')
const typePipeline = lookupPipeline('candidate_types', 'type')
const reviewedByPipeline = lookupPipeline('hiring_managers', 'resume.reviewed_by')
const createdByPipeline = lookupPipeline('users', 'createdBy')
const updatedByPipeline = lookupPipeline('users', 'updatedBy')
const distributionBasePipeline = lookupPipeline('distributions', 'distribution')
const distributionPipeline = [
  ...distributionBasePipeline,
  ...lookupPipeline('distribution_subcategories', 'distribution.subcategory')
]
const distributionCategoryPipeline = [
  ...distributionBasePipeline,
  ...lookupPipeline('distribution_categories', 'distribution.category')
]
const positionPipeline = lookupPipeline('positions', 'position', '_id')
const positionContractPipeline = [
  ...positionPipeline,
  ...lookupPipeline('cat_contracts', 'position.contract')
]
const positionStatusPipeline = [
  ...positionPipeline,
  ...lookupPipeline('position_statuses', 'position.status')
]
const positionProgramManagerPipeline = [
  ...positionPipeline,
  ...lookupPipeline('program_managers', 'position.program_manager')
]
const positionHiringManagerPipeline = [
  ...positionPipeline,
  ...lookupPipeline('hiring_managers', 'position.hiring_manager')
]

const requiredFields = ['name.first', 'name.last']

const fields: CustomField[] = [
  // Identity
  { name: 'name.first', label: 'First Name', ...fieldProps.text, render: renderCandidateLink },
  { name: 'name.last', label: 'Last Name', ...fieldProps.text, render: renderCandidateLink },
  { name: 'firstLastName', label: 'First Last Name', ...fieldProps.text },
  { name: 'lastFirstName', label: 'Last, First Name', ...fieldProps.text },
  { name: 'type.name', label: 'Type', pipeline: typePipeline, ...fieldProps.text },
  { name: 'reapply', label: 'Reapply', ...fieldProps.boolean },
  { name: 'position.id', label: 'Position ID', pipeline: positionPipeline, ...fieldProps.text },
  { name: 'position.title', label: 'Position Title', pipeline: positionPipeline, ...fieldProps.text },
  { name: 'position.contract.name', label: 'Contract Name', pipeline: positionContractPipeline, ...fieldProps.text },
  { name: 'position.contract.abbreviation', label: 'Contract Abbreviation', pipeline: positionContractPipeline, ...fieldProps.text },
  { name: 'position.status.name', label: 'Position Status', pipeline: positionStatusPipeline, ...fieldProps.text },
  { name: 'position.program_manager.name', label: 'Position Program Manager', pipeline: positionProgramManagerPipeline, ...fieldProps.text },
  { name: 'position.hiring_manager.name', label: 'Position Hiring Manager', pipeline: positionHiringManagerPipeline, ...fieldProps.text },
  { name: 'distribution.category.name', label: 'Distribution Category', pipeline: distributionCategoryPipeline, ...fieldProps.text },
  { name: 'distribution.subcategory.name', label: 'Distribution Subcategory', pipeline: distributionPipeline, ...fieldProps.text },

  // Contact
  { name: 'location', label: 'Location', ...fieldProps.text },
  { name: 'email', label: 'Email', ...fieldProps.text },
  { name: 'phone', label: 'Phone', ...fieldProps.text },
  { name: 'address.street', label: 'Address Street', ...fieldProps.text },
  { name: 'address.city', label: 'Address City', ...fieldProps.text },
  { name: 'address.state', label: 'Address State', ...fieldProps.text },
  { name: 'address.zip', label: 'Address Zip', ...fieldProps.text },

  // Classification
  { name: 'status.name', label: 'Status', pipeline: statusPipeline, ...fieldProps.text },
  { name: 'substatus.name', label: 'Substatus', pipeline: substatusPipeline, ...fieldProps.text },
  { name: 'rank.name', label: 'Rank', pipeline: rankPipeline, ...fieldProps.text },
  { name: 'clearance.name', label: 'Clearance', pipeline: clearancePipeline, ...fieldProps.text },

  // Compensation
  { name: 'bill_rate.min', label: 'Bill Rate Min', ...fieldProps.number },
  { name: 'bill_rate.max', label: 'Bill Rate Max', ...fieldProps.number },
  { name: 'salary.min', label: 'Salary Min', ...fieldProps.number },
  { name: 'salary.max', label: 'Salary Max', ...fieldProps.number },

  // Notes
  { name: 'notes', label: 'Notes', ...fieldProps.text },

  // Dates
  { name: 'received_on', label: 'Received On', ...fieldProps.date },
  { name: 'rejection_date', label: 'Rejection Date', ...fieldProps.date },
  { name: 'resume.sent', label: 'Resume Sent', ...fieldProps.date },
  { name: 'resume.reviewed', label: 'Resume Reviewed', ...fieldProps.date },
  { name: 'resume.reviewed_by.name', label: 'Resume Reviewed By', pipeline: reviewedByPipeline, ...fieldProps.text },
  { name: 'round_1', label: 'Round 1', ...fieldProps.date },
  { name: 'round_2', label: 'Round 2', ...fieldProps.date },
  { name: 'round_3', label: 'Round 3', ...fieldProps.date },

  // Exhibit
  { name: 'exhibit.sent_for_review', label: 'Exhibit Sent for Review', ...fieldProps.date },
  { name: 'exhibit.hm_approved', label: 'Exhibit HM Approved', ...fieldProps.date },
  { name: 'exhibit.contracts_approved', label: 'Exhibit Contracts Approved', ...fieldProps.date },
  { name: 'exhibit.signed', label: 'Exhibit Signed', ...fieldProps.date },
  { name: 'exhibit.pmo_approved', label: 'Exhibit PMO Approved', ...fieldProps.date },
  { name: 'exhibit.extended', label: 'Exhibit Extended', ...fieldProps.date },

  // Offer Letter
  { name: 'offer_letter.approved', label: 'Offer Letter Approved', ...fieldProps.date },
  { name: 'offer_letter.extended', label: 'Offer Letter Extended', ...fieldProps.date },
  { name: 'offer_letter.signed', label: 'Offer Letter Signed', ...fieldProps.date },

  // NDA
  { name: 'nda_signed.staffing_agency', label: 'NDA Signed - Staffing Agency', ...fieldProps.date },
  { name: 'nda_signed.candidate', label: 'NDA Signed - Candidate', ...fieldProps.date },

  { name: 'createdBy.name', label: 'Created By', pipeline: createdByPipeline, ...fieldProps.text },
  { name: 'createdAt', label: 'Created At', ...fieldProps.date },
  { name: 'updatedBy.name', label: 'Updated By', pipeline: updatedByPipeline, ...fieldProps.text },
  { name: 'updatedAt', label: 'Updated At', ...fieldProps.date }
]

export const config = {
  fields,
  requiredFields,
  requiredFieldObjects: fields.filter(f => requiredFields.includes(f.name))
}
